import { UploadCloud, FileIcon, X, AlertCircle } from 'lucide-react';
import { useFileUpload } from '../../hooks/use-file-upload.hook';
import { FileData } from '../../types/forms';

interface FileUploadProps {
  acceptedTypes?: string;
  onChange: (files: FileData[]) => void;
  disabled?: boolean;
  existingFiles?: FileData[];
  isInvalid?: boolean;
}

// Purpose: Handles file upload UI with drag and drop functionality
export const FileUpload = ({
  acceptedTypes = 'application/pdf',
  onChange,
  disabled = false,
  existingFiles = [],
  isInvalid = false,
}: FileUploadProps) => {
  const {
    uploadingFiles,
    isDragging,
    fileTypeError,
    handleFileChange,
    handleRemoveFile,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    displayTypes,
  } = useFileUpload({
    acceptedTypes,
    onChange,
    existingFiles,
  });

  const hasError = isInvalid || !!fileTypeError;

  return (
    <div className="space-y-4">
      <div
        className={`flex flex-col items-center justify-center p-6 border-2 border-dashed rounded-lg transition-colors
          ${isDragging ? 'border-blue-500 bg-blue-50' : hasError ? 'border-red-500 bg-red-50' : 'border-gray-300'} 
          ${disabled ? 'cursor-not-allowed opacity-50' : hasError ? 'hover:border-red-600' : 'hover:border-blue-500'}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <input
          type="file"
          className="hidden"
          accept={acceptedTypes}
          onChange={handleFileChange}
          id="file-upload"
          multiple
          disabled={disabled}
        />
        <label
          htmlFor="file-upload"
          className={`cursor-pointer flex flex-col items-center ${disabled ? 'cursor-not-allowed' : ''}`}
        >
          <UploadCloud className={`h-8 w-8 mb-2 ${hasError ? 'text-red-400' : 'text-gray-400'}`} />
          <span className={`text-sm ${hasError ? 'text-red-500' : 'text-gray-500'}`}>
            Click to upload or drag and drop
          </span>
          <span className="text-xs text-gray-400 mt-1">{displayTypes} supported</span>
        </label>
      </div>

      {/* Display File Type Error */}
      {fileTypeError && (
        <div className="text-sm text-red-600 flex items-center">
          <AlertCircle className="h-4 w-4 mr-1 flex-shrink-0" />
          <span>{fileTypeError}</span>
        </div>
      )}

      {uploadingFiles.length > 0 && (
        <div className="space-y-2">
          {uploadingFiles.map((file, index) => (
            <div
              key={index}
              className={`flex items-center space-x-3 p-3 bg-white rounded-lg border ${fileTypeError || isInvalid ? 'border-red-300' : 'border-gray-200'}`}
            >
              <FileIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex justify-between items-center mb-1">
                  <span className="text-sm font-medium text-gray-700 truncate">{file.name}</span>
                  <button
                    onClick={() => handleRemoveFile(index)}
                    className="p-1 hover:bg-gray-100 rounded-full"
                    disabled={disabled}
                  >
                    <X className="h-4 w-4 text-gray-500" />
                  </button>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-1.5">
                  <div
                    className={`h-1.5 rounded-full transition-all duration-300 ${
                      file.status === 'error'
                        ? 'bg-red-500'
                        : file.status === 'complete'
                          ? 'bg-green-500'
                          : 'bg-blue-500'
                    }`}
                    style={{ width: `${file.progress}%` }}
                  />
                </div>
                <span className="text-xs text-gray-500 mt-1">
                  {file.status === 'error'
                    ? 'Upload failed'
                    : file.status === 'complete'
                      ? 'Upload complete'
                      : `Uploading... ${file.progress}%`}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
